import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { formatUserName, getEmergencyAdminWarning } from '../../utils/userDisplayUtils';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Menu,
  MenuItem,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  AccountCircle,
  ExitToApp,
  Dashboard,
  GolfCourse,
  People,
  History,
  Settings,
  GolfCourseOutlined,
  Person,
  Security,
  Storage,
  Assessment,
  Menu as MenuIcon,
  Close
} from '@mui/icons-material';

export default function Navbar() {
  const { userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [settingsAnchorEl, setSettingsAnchorEl] = React.useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = React.useState(false);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSettingsMenu = (event) => {
    setSettingsAnchorEl(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchorEl(null);
  };

  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleMobileNavigation = (path) => {
    navigate(path);
    setMobileDrawerOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out', error);
    }
    handleClose();
    setMobileDrawerOpen(false);
  };

  // Navigation items
  const navigationItems = [
    { label: 'Dashboard', path: '/dashboard', icon: <Dashboard /> },
    { label: 'Tournaments', path: '/tournaments', icon: <GolfCourse /> },
    { label: 'Players', path: '/players', icon: <People /> },
    { label: 'Golf Courses', path: '/golf-courses', icon: <GolfCourseOutlined /> },
  ];

  const settingsItems = [
    { label: 'General Settings', path: '/settings', icon: <Settings /> },
    { label: 'My Profile', path: '/settings/profile', icon: <Person /> },
  ];

  const adminSettingsItems = [
    { label: 'User Management', path: '/settings/users', icon: <Security /> },
    { label: 'Transaction Log', path: '/settings/transaction-log', icon: <Assessment /> },
    { label: 'Backups & Restore', path: '/settings/backups-restore', icon: <Storage /> },
  ];

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleMobileDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Golf Tournament Admin
          </Typography>
          
          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
              {navigationItems.map((item) => (
                <Button
                  key={item.path}
                  color="inherit"
                  startIcon={item.icon}
                  onClick={() => navigate(item.path)}
                  sx={{ minWidth: 'auto', px: 1 }}
                >
                  {item.label}
                </Button>
              ))}
              <Button
                color="inherit"
                startIcon={<Settings />}
                onClick={handleSettingsMenu}
                sx={{ minWidth: 'auto', px: 1 }}
              >
                Settings
              </Button>
            </Box>
          )}

          {/* User Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {!isMobile && (
              <Box sx={{ mr: 2 }}>
                {formatUserName(userProfile, { 
                  variant: 'body2',
                  showIcon: false 
                })}
              </Box>
            )}
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              {isMobile && (
                <>
                  <Box sx={{ px: 2, py: 1 }}>
                    {formatUserName(userProfile, { 
                      variant: 'subtitle2',
                      color: 'text.secondary',
                      showIcon: false 
                    })}
                  </Box>
                  <Divider />
                </>
              )}
              <MenuItem onClick={handleLogout}>
                <ExitToApp sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </Menu>

            {/* Desktop Settings Menu */}
            {!isMobile && (
              <Menu
                id="settings-menu"
                anchorEl={settingsAnchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(settingsAnchorEl)}
                onClose={handleSettingsClose}
              >
                {settingsItems.map((item) => (
                  <MenuItem key={item.path} onClick={() => { navigate(item.path); handleSettingsClose(); }}>
                    {item.icon}
                    <ListItemText sx={{ ml: 1 }}>{item.label}</ListItemText>
                  </MenuItem>
                ))}
                {userProfile?.role === 'admin' && (
                  <>
                    <Divider />
                    {adminSettingsItems.map((item) => (
                      <MenuItem key={item.path} onClick={() => { navigate(item.path); handleSettingsClose(); }}>
                        {item.icon}
                        <ListItemText sx={{ ml: 1 }}>{item.label}</ListItemText>
                      </MenuItem>
                    ))}
                  </>
                )}
              </Menu>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileDrawerOpen}
        onClose={handleMobileDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Menu</Typography>
          <IconButton onClick={handleMobileDrawerToggle}>
            <Close />
          </IconButton>
        </Box>
        <Divider />
        
        <List>
          {navigationItems.map((item) => (
            <ListItem
              key={item.path}
              button
              onClick={() => handleMobileNavigation(item.path)}
              sx={{ py: 1 }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItem>
          ))}
        </List>

        <Divider />
        
        <List>
          <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
            Settings
          </Typography>
          {settingsItems.map((item) => (
            <ListItem
              key={item.path}
              button
              onClick={() => handleMobileNavigation(item.path)}
              sx={{ py: 1 }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItem>
          ))}
          
          {userProfile?.role === 'admin' && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
                Admin
              </Typography>
              {adminSettingsItems.map((item) => (
                <ListItem
                  key={item.path}
                  button
                  onClick={() => handleMobileNavigation(item.path)}
                  sx={{ py: 1 }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItem>
              ))}
            </>
          )}
        </List>
      </Drawer>
    </>
  );
}
